import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Row, Col, Card, Select, Typography, Spin, message, Empty, theme,
  InputNumber, DatePicker, Button, Modal, Form, Input, Tag, Avatar,
  Tooltip, Table, List, Checkbox, Popconfirm, Tabs,
} from 'antd';
import {
  PlusOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  CalendarOutlined,
  LeftOutlined,
  RightOutlined,
  EditOutlined,
  DeleteOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import { eventoService, ventaService, animalService, fixUrl } from '../../services/api';
import GrillaTransitoria from './TransitoriaRemate';
import MatchVenta from './MatchVenta';
import SelectComprador from './SelectComprador';
import { money } from '../../utils/format';
import { descargarBlob } from '../../utils/descargas';
import { formatearMonto, parsearMonto } from '../../utils/montoInput';
import { eventosDeListaCorta } from '../../utils/listaCorta';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

// ── Cascada de imagen: foto principal → captura de YouTube → placeholder ──────
const youtubeThumb = (url) => {
  const m = url?.match(/(?:v=|youtu\.be\/|embed\/)([^&?/]+)/);
  return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
};

const resolverImagenVenta = (animal) => {
  const foto = fixUrl(animal?.imagen_principal?.url_web || animal?.imagen_principal?.url_imagen);
  return foto || youtubeThumb(animal?.codigo_video) || null;
};

// Imagen del animal con la cascada + placeholder si todo falla.
const ImagenAnimal = ({ animal }) => {
  const { token } = theme.useToken();
  const [error, setError] = useState(false);
  const url = resolverImagenVenta(animal);
  if (!url || error) {
    return (
      <div style={{ width: '100%', minHeight: 160, borderRadius: 6, background: token.colorFillSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Avatar size={64} icon={<UserOutlined />} style={{ background: token.colorFillTertiary }} />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={animal.nom_animal}
      onError={() => setError(true)}
      style={{ width: '100%', borderRadius: 6 }}
    />
  );
};

// Tags comunes de un animal.
const TagsAnimal = ({ animal }) => (
  <div style={{ marginBottom: 8 }}>
    {animal.nom_raza && <Tag color="blue">{animal.nom_raza}</Tag>}
    {animal.categoria?.nom_categoria && <Tag color="geekblue">{animal.categoria.nom_categoria}</Tag>}
    {animal.rp_animal && <Tag>RP: {animal.rp_animal}</Tag>}
    {animal.fecha_nac && <Tag icon={<CalendarOutlined />}>{dayjs(animal.fecha_nac).format('DD/MM/YYYY')}</Tag>}
    {animal.peso_actual && <Tag>{animal.peso_actual} kg</Tag>}
  </div>
);

// Header con navegación prev/next entre unidades (corrales / animales sueltos).
const NavHeader = ({ subtitulo, titulo, currentIndex, total, onPrev, onNext }) => (
  <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
    <Col>
      {subtitulo && <Text strong style={{ fontSize: 16, display: 'block' }}>{subtitulo}</Text>}
      <Text strong style={{ fontSize: 16, display: 'block' }}>{titulo}</Text>
    </Col>
    <Col>
      <Button.Group>
        <Button icon={<LeftOutlined />} disabled={currentIndex === 0} onClick={onPrev} />
        <Button disabled style={{ pointerEvents: 'none', minWidth: 60, textAlign: 'center' }}>
          {currentIndex + 1} / {total}
        </Button>
        <Button icon={<RightOutlined />} disabled={currentIndex === total - 1} onClick={onNext} />
      </Button.Group>
    </Col>
  </Row>
);

// ── Modal registro rápido de comprador ──────────────────────────────────────
const ModalRegistroRapido = ({ open, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleGuardar = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const res = await ventaService.registroRapido(values);
      message.success(res.data.message || 'Usuario creado correctamente');
      form.resetFields();
      onSuccess(res.data.usuario);
    } catch (err) {
      if (err?.errorFields) return;
      const msg = err?.response?.data?.error || 'Error al crear usuario';
      if (err?.response?.status === 409) {
        message.warning(msg);
        onSuccess(err.response.data.usuario);
      } else {
        message.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Registrar comprador rápido"
      open={open}
      onCancel={onClose}
      onOk={handleGuardar}
      confirmLoading={loading}
      okText="Crear usuario"
      cancelText="Cancelar"
      destroyOnHidden
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item name="nom_usuario" label="Nombre completo" rules={[{ required: true, message: 'Requerido' }]}>
          <Input placeholder="Juan Pérez" />
        </Form.Item>
        <Form.Item name="email_usuario" label="Email" rules={[{ required: true, message: 'Requerido' }, { type: 'email', message: 'Email inválido' }]}>
          <Input placeholder="juan@ejemplo.com" />
        </Form.Item>
        <Form.Item name="cuit" label="CUIT / DNI" rules={[{ required: true, message: 'Requerido' }]} extra="Se usará como contraseña inicial">
          <Input placeholder="20123456789" maxLength={11} />
        </Form.Item>
        <Form.Item name="telefono" label="Teléfono (opcional)">
          <Input placeholder="+54 9 11 1234-5678" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ── Formulario de venta (agnóstico del destino) ──────────────────────────────
// Recolecta comprador/importe/%/fecha y delega el alta en `onCargarVenta`.
// Así se reutiliza tanto para venta individual (ventaService.crear) como para
// venta agrupada de corral (ventaService.crearCorral).
const VentaForm = ({ onCargarVenta, onVentaCreada, disabled = false, submitLabel = 'Cargar Venta', hintImporte }) => {
  const { token } = theme.useToken();
  const [extraUsuarios, setExtraUsuarios] = useState([]);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [importe, setImporte] = useState(null);
  const [porcentaje, setPorcentaje] = useState(100);
  const [fechaVenta, setFechaVenta] = useState(dayjs());
  const [loadingVenta, setLoadingVenta] = useState(false);
  const [modalRegistroOpen, setModalRegistroOpen] = useState(false);

  const handleCargarVenta = async () => {
    if (!selectedUsuario) { message.warning('Seleccioná un comprador'); return; }
    if (!porcentaje || porcentaje <= 0 || porcentaje > 100) { message.warning('El porcentaje debe ser entre 1 y 100'); return; }
    setLoadingVenta(true);
    try {
      await onCargarVenta({
        id_usuario: selectedUsuario,
        porcentaje_venta: porcentaje,
        importe_venta: importe,
        fecha_venta: fechaVenta ? fechaVenta.format('YYYY-MM-DD') : null,
      });
      message.success('Venta cargada correctamente');
      setSelectedUsuario(null);
      setExtraUsuarios([]);
      setImporte(null);
      setPorcentaje(100);
      setFechaVenta(dayjs());
      onVentaCreada?.();
    } catch (err) {
      message.error(err?.response?.data?.error || 'Error al cargar la venta');
    } finally {
      setLoadingVenta(false);
    }
  };

  const handleNuevoCompradorCreado = (usuario) => {
    setExtraUsuarios((prev) => [usuario, ...prev]);
    setSelectedUsuario(usuario.id_usuario);
    setModalRegistroOpen(false);
  };

  return (
    <>
      <div style={{ background: token.colorFillAlter, borderRadius: 6, padding: '12px' }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
          Cargar venta
        </Text>
        <Row gutter={8} style={{ marginBottom: 8 }}>
          <Col flex="auto">
            <SelectComprador
              value={selectedUsuario}
              onChange={setSelectedUsuario}
              extraUsuarios={extraUsuarios}
            />
          </Col>
          <Col>
            <Tooltip title="Registrar comprador nuevo">
              <Button icon={<PlusOutlined />} onClick={() => setModalRegistroOpen(true)} />
            </Tooltip>
          </Col>
        </Row>
        <Row gutter={8} style={{ marginBottom: hintImporte ? 2 : 8 }}>
          <Col xs={12}>
            <InputNumber placeholder="Importe" value={importe} onChange={setImporte} prefix="$" style={{ width: '100%' }} min={0}
                formatter={formatearMonto} parser={parsearMonto} />
          </Col>
          <Col xs={12}>
            <InputNumber placeholder="Porcentaje" value={porcentaje} onChange={setPorcentaje} suffix="%" style={{ width: '100%' }} min={1} max={100} />
          </Col>
        </Row>
        {hintImporte && (
          <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 11 }}>{hintImporte}</Text>
        )}
        <Row gutter={8} align="middle">
          <Col flex="auto">
            <DatePicker value={fechaVenta} onChange={setFechaVenta} format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Fecha de venta" />
          </Col>
          <Col>
            <Button type="primary" icon={<ShoppingCartOutlined />} loading={loadingVenta} disabled={disabled} onClick={handleCargarVenta}>
              {submitLabel}
            </Button>
          </Col>
        </Row>
      </div>
      <ModalRegistroRapido
        open={modalRegistroOpen}
        onClose={() => setModalRegistroOpen(false)}
        onSuccess={handleNuevoCompradorCreado}
      />
    </>
  );
};

// ── Lista de ventas ya cargadas de un animal (read-only + editar) ────────────
const VentasCargadas = ({ ventas, onEditar }) => {
  if (!ventas || ventas.length === 0) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
        Ventas cargadas
      </Text>
      <List
        size="small"
        bordered
        dataSource={ventas}
        renderItem={(v) => (
          <List.Item
            actions={[
              <Button key="edit" size="small" icon={<EditOutlined />} onClick={() => onEditar(v)}>
                Editar
              </Button>,
            ]}
          >
            <div>
              <Text strong>{v.usuario?.nom_usuario || 'Comprador'}</Text>{' '}
              <Tag>{v.porcentaje_venta}%</Tag>
              {v.importe_venta != null && (
                <Tag color="green">{money(v.importe_venta)}</Tag>
              )}
              {v.fecha_venta && (
                <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(v.fecha_venta).format('DD/MM/YYYY')}</Text>
              )}
            </div>
          </List.Item>
        )}
      />
    </div>
  );
};

// ── Modal para editar / eliminar una venta existente ─────────────────────────
const ModalEditarVenta = ({ venta, onClose, onGuardado }) => {
  const [extraUsuarios, setExtraUsuarios] = useState([]);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [importe, setImporte] = useState(null);
  const [porcentaje, setPorcentaje] = useState(100);
  const [fecha, setFecha] = useState(dayjs());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Pre-cargar los datos de la venta al abrir.
  useEffect(() => {
    if (venta) {
      setSelectedUsuario(venta.id_usuario);
      setExtraUsuarios(venta.usuario ? [venta.usuario] : []);
      setImporte(venta.importe_venta ?? null);
      setPorcentaje(venta.porcentaje_venta ?? 100);
      setFecha(venta.fecha_venta ? dayjs(venta.fecha_venta) : dayjs());
    }
  }, [venta]);

  const guardar = async () => {
    if (!selectedUsuario) { message.warning('Seleccioná un comprador'); return; }
    if (!porcentaje || porcentaje <= 0 || porcentaje > 100) { message.warning('El porcentaje debe ser entre 1 y 100'); return; }
    setSaving(true);
    try {
      await ventaService.editar(venta.id_venta, {
        id_usuario: selectedUsuario,
        porcentaje_venta: porcentaje,
        importe_venta: importe,
        fecha_venta: fecha ? fecha.format('YYYY-MM-DD') : null,
      });
      message.success('Venta actualizada');
      onGuardado?.();
      onClose();
    } catch (err) {
      message.error(err?.response?.data?.error || 'Error al editar la venta');
    } finally {
      setSaving(false);
    }
  };

  const eliminar = async () => {
    setDeleting(true);
    try {
      await ventaService.eliminar(venta.id_venta);
      message.success('Venta eliminada');
      onGuardado?.();
      onClose();
    } catch (err) {
      message.error(err?.response?.data?.error || 'Error al eliminar la venta');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      title="Editar venta"
      open={!!venta}
      onCancel={onClose}
      destroyOnHidden
      width={520}
      footer={[
        <Popconfirm
          key="del"
          title="¿Eliminar esta venta?"
          description="Se borra el registro de venta de este animal."
          okText="Eliminar"
          cancelText="Cancelar"
          okButtonProps={{ danger: true }}
          onConfirm={eliminar}
        >
          <Button danger loading={deleting} icon={<DeleteOutlined />} style={{ float: 'left' }}>Eliminar</Button>
        </Popconfirm>,
        <Button key="cancel" onClick={onClose}>Cancelar</Button>,
        <Button key="save" type="primary" loading={saving} onClick={guardar}>Guardar cambios</Button>,
      ]}
    >
      {venta && (
        <div style={{ paddingTop: 8 }}>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Comprador</Text>
            <SelectComprador
              value={selectedUsuario}
              onChange={setSelectedUsuario}
              extraUsuarios={extraUsuarios}
            />
          </div>
          <Row gutter={8} style={{ marginBottom: 8 }}>
            <Col xs={12}>
              <InputNumber placeholder="Importe" value={importe} onChange={setImporte} prefix="$" style={{ width: '100%' }} min={0}
                formatter={formatearMonto} parser={parsearMonto} />
            </Col>
            <Col xs={12}>
              <InputNumber placeholder="Porcentaje" value={porcentaje} onChange={setPorcentaje} suffix="%" style={{ width: '100%' }} min={1} max={100} />
            </Col>
          </Row>
          <DatePicker value={fecha} onChange={setFecha} format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Fecha de venta" />
        </div>
      )}
    </Modal>
  );
};

// ── Tarjeta de animal suelto (no agrupado) ───────────────────────────────────
const AnimalCard = ({ animal, onVentaCreada, onEditarVenta, currentIndex, total, onPrev, onNext }) => {
  const pct = animal.pct_vendido || 0;
  return (
  <Card style={{ borderRadius: 8 }} styles={{ body: { padding: '16px' } }}>
    <Row gutter={[16, 16]} align="top">
      <Col xs={24} md={10}>
        <ImagenAnimal animal={animal} />
      </Col>
      <Col xs={24} md={14}>
        <NavHeader
          subtitulo={animal.corral != null ? `Corral ${animal.corral}` : null}
          titulo={animal.nom_animal}
          currentIndex={currentIndex}
          total={total}
          onPrev={onPrev}
          onNext={onNext}
        />
        <TagsAnimal animal={animal} />
        <VentasCargadas ventas={animal.ventas} onEditar={onEditarVenta} />
        {pct < 100 ? (
          <VentaForm
            onVentaCreada={onVentaCreada}
            submitLabel={pct > 0 ? `Vender ${100 - pct}% restante` : 'Cargar Venta'}
            onCargarVenta={(payload) => ventaService.crear({ id_animal: animal.id_animal, ...payload })}
          />
        ) : (
          <Tag color="red" style={{ marginTop: 4 }}>Vendido 100%</Tag>
        )}
      </Col>
    </Row>
  </Card>
  );
};

// ── Tarjeta de corral agrupado (venta de todo el corral o elección) ──────────
const CorralCard = ({ corral, animales, onVentaCreada, onEditarVenta, currentIndex, total, onPrev, onNext }) => {
  const representativo = animales.find((a) => resolverImagenVenta(a)) || animales[0];

  // Un animal está "vendido" (no seleccionable) si su % acumulado ya es 100.
  const disponibles = animales.filter((a) => (a.pct_vendido || 0) < 100);
  // Por defecto: todos los disponibles tildados (la venta aplica a TODO el corral).
  const [seleccionados, setSeleccionados] = useState(() => disponibles.map((a) => a.id_animal));

  // Tras una venta, `animales` se recarga con pct_vendido actualizado: quitamos
  // de la selección los que quedaron vendidos al 100% (así no se reintentan).
  useEffect(() => {
    const idsDisponibles = new Set(
      animales.filter((a) => (a.pct_vendido || 0) < 100).map((a) => a.id_animal)
    );
    setSeleccionados((prev) => prev.filter((id) => idsDisponibles.has(id)));
  }, [animales]);

  const toggle = (id, checked) => {
    setSeleccionados((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const todosTildados = seleccionados.length === disponibles.length && disponibles.length > 0;
  const toggleTodos = (checked) => {
    setSeleccionados(checked ? disponibles.map((a) => a.id_animal) : []);
  };

  return (
    <Card style={{ borderRadius: 8 }} styles={{ body: { padding: '16px' } }}>
      <Row gutter={[16, 16]} align="top">
        <Col xs={24} md={10}>
          <ImagenAnimal animal={representativo} />
        </Col>
        <Col xs={24} md={14}>
          <NavHeader
            subtitulo="Corral agrupado"
            titulo={`Corral ${corral} — ${animales.length} animales`}
            currentIndex={currentIndex}
            total={total}
            onPrev={onPrev}
            onNext={onNext}
          />

          {representativo?.categoria?.nom_categoria && (
            <div style={{ marginBottom: 8 }}>
              {representativo.nom_raza && <Tag color="blue">{representativo.nom_raza}</Tag>}
              <Tag color="geekblue">{representativo.categoria.nom_categoria}</Tag>
            </div>
          )}

          {/* Lista de animales del corral con checkboxes */}
          <div style={{ marginBottom: 12 }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 6 }}>
              <Col>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  La venta se aplica a los animales tildados. Destildá para vender por elección.
                </Text>
              </Col>
              <Col>
                {disponibles.length > 0 && (
                  <Checkbox checked={todosTildados} onChange={(e) => toggleTodos(e.target.checked)}>
                    <Text style={{ fontSize: 12 }}>Todos</Text>
                  </Checkbox>
                )}
              </Col>
            </Row>
            <List
              size="small"
              bordered
              dataSource={animales}
              style={{ maxHeight: 220, overflowY: 'auto' }}
              renderItem={(a) => {
                const vendido = (a.pct_vendido || 0) >= 100;
                return (
                  <List.Item>
                    <Row style={{ width: '100%' }} align="middle" justify="space-between">
                      <Col>
                        <Checkbox
                          disabled={vendido}
                          checked={seleccionados.includes(a.id_animal)}
                          onChange={(e) => toggle(a.id_animal, e.target.checked)}
                        >
                          <Text delete={vendido}>{a.nom_animal}</Text>
                        </Checkbox>
                      </Col>
                      <Col>
                        <Text type="secondary" style={{ marginRight: 8 }}>RP: {a.rp_animal}</Text>
                        {vendido && <Tag color="red">Vendido</Tag>}
                        {!vendido && (a.pct_vendido || 0) > 0 && <Tag color="orange">{a.pct_vendido}% vendido</Tag>}
                        {a.ventas?.length > 0 && (
                          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => onEditarVenta(a.ventas[0])}>
                            Editar
                          </Button>
                        )}
                      </Col>
                    </Row>
                  </List.Item>
                );
              }}
            />
          </div>

          <VentaForm
            onVentaCreada={onVentaCreada}
            disabled={seleccionados.length === 0}
            submitLabel={`Vender ${seleccionados.length} animal${seleccionados.length === 1 ? '' : 'es'}`}
            hintImporte="El importe se carga a cada animal seleccionado."
            onCargarVenta={(payload) => ventaService.crearCorral({ ids_animales: seleccionados, ...payload })}
          />
        </Col>
      </Row>
    </Card>
  );
};

// ── Modal de venta (modo todos) ──────────────────────────────────────────────
const ModalVenta = ({ animal, onClose, onVentaCreada, onEditarVenta }) => {
  const handleVentaCreada = () => {
    onVentaCreada?.();
    onClose();
  };

  const pct = animal?.pct_vendido || 0;

  return (
    <Modal
      title={animal ? `Vender — ${animal.nom_animal}` : ''}
      open={!!animal}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={560}
    >
      {animal && (
        <div style={{ paddingTop: 8 }}>
          <div style={{ marginBottom: 12 }}>
            {animal.nom_raza && <Tag color="blue">{animal.nom_raza}</Tag>}
            {animal.categoria?.nom_categoria && <Tag color="geekblue">{animal.categoria.nom_categoria}</Tag>}
            {animal.rp_animal && <Tag>RP: {animal.rp_animal}</Tag>}
            {animal.corral && <Tag>Corral: {animal.corral}</Tag>}
          </div>
          <VentasCargadas ventas={animal.ventas} onEditar={onEditarVenta} />
          {pct < 100 ? (
            <VentaForm
              onVentaCreada={handleVentaCreada}
              submitLabel={pct > 0 ? `Vender ${100 - pct}% restante` : 'Cargar Venta'}
              onCargarVenta={(payload) => ventaService.crear({ id_animal: animal.id_animal, ...payload })}
            />
          ) : (
            <Tag color="red">Vendido 100%</Tag>
          )}
        </div>
      )}
    </Modal>
  );
};

// Agrupa la lista plana de animales en unidades de navegación:
// - corrales agrupados → una unidad por corral (con sus animales)
// - animales sueltos → una unidad por animal
const construirUnidades = (animales) => {
  const unidades = [];
  const corralIdx = new Map();
  for (const a of animales) {
    if (a.agrupado && a.corral != null) {
      if (corralIdx.has(a.corral)) {
        unidades[corralIdx.get(a.corral)].animales.push(a);
      } else {
        corralIdx.set(a.corral, unidades.length);
        unidades.push({ tipo: 'corral', corral: a.corral, animales: [a] });
      }
    } else {
      unidades.push({ tipo: 'animal', animal: a });
    }
  }
  return unidades;
};

// ── Página principal ─────────────────────────────────────────────────────────
// ── Totales del registro ─────────────────────────────────────────────────────
// Lo VENDIDO de un animal es la suma de los importes de sus ventas cargadas,
// tal como están: un animal vendido en dos mitades a $500.000 cada una suma
// $1.000.000. No se "lleva al 100%" como el resumen del PDF de ventas
// transitorias, porque ese es un valor de mercado y esto es caja.
const importeVendido = (animal) =>
  (animal.ventas || []).reduce((acc, v) => acc + (Number(v.importe_venta) || 0), 0);

// Disponible / parcial / vendido, a partir del porcentaje vendido. Lo usan el
// chip de la columna Estado y el filtro de estado: UNA regla, no dos.
const estadoVenta = (animal) => {
  const pct = animal.pct_vendido || 0;
  if (pct >= 100) return 'vendido';
  if (pct > 0) return 'parcial';
  return 'disponible';
};
const ESTADOS_VENTA = [
  { value: 'disponible', label: 'Disponible' },
  { value: 'parcial', label: 'Parcial' },
  { value: 'vendido', label: 'Vendido' },
];

// Subtotales por categoría y, adentro de cada una, por raza; más el total
// general. Pedido del usuario (2026-10-07): "que tome subtotales por categoría
// y raza y genere un total de ventas". Se calcula sobre las filas que se VEN
// —filtradas— para que el número de abajo coincida con la tabla de arriba.
//
// Las filas llegan aplanadas, con `nivel` 0 (categoría) y 1 (raza), igual que
// `utils/resumen_agrupado.aplanar_resumen` del backend: es el mismo molde que
// ya usan el Excel y el PDF, y así los tres se leen igual. Eso incluye SU
// regla de la raza única: una categoría con una sola raza NO se abre, la raza
// se pega al nombre ("Toros · Brangus"). No es un caso de borde —en el remate
// de Pilagá en Mercedes las seis categorías tienen una sola raza— y sin la
// regla el panel mostraba doce filas donde el PDF muestra seis, cada hija
// repitiendo número por número a la madre.
//
// El separador es el MISMO que `SEPARADOR_RAZA_UNICA` del backend. Está
// repetido y no importado porque vive en Python; si allá cambia, cambia acá.
const SEPARADOR_RAZA_UNICA = ' · ';

const resumirVentas = (animales) => {
  const porCategoria = new Map();
  let totalImporte = 0;
  let totalVendidos = 0;
  animales.forEach((a) => {
    const importe = importeVendido(a);
    const vendido = (a.pct_vendido || 0) > 0;
    if (!vendido && !importe) return;
    const cat = a.categoria?.nom_categoria || 'Sin categoría';
    const raza = a.nom_raza || 'Sin raza';
    if (!porCategoria.has(cat)) porCategoria.set(cat, { vendidos: 0, importe: 0, razas: new Map() });
    const c = porCategoria.get(cat);
    if (!c.razas.has(raza)) c.razas.set(raza, { vendidos: 0, importe: 0 });
    const r = c.razas.get(raza);
    c.vendidos += 1; c.importe += importe;
    r.vendidos += 1; r.importe += importe;
    totalVendidos += 1; totalImporte += importe;
  });
  const filas = [];
  [...porCategoria.entries()]
    .sort(([x], [y]) => x.localeCompare(y, 'es'))
    .forEach(([cat, c]) => {
      const unica = c.razas.size === 1 ? [...c.razas.keys()][0] : null;
      filas.push({
        key: `c-${cat}`, nivel: 0,
        grupo: unica ? `${cat}${SEPARADOR_RAZA_UNICA}${unica}` : cat,
        vendidos: c.vendidos, importe: c.importe,
      });
      if (unica) return;
      [...c.razas.entries()]
        .sort(([x], [y]) => x.localeCompare(y, 'es'))
        .forEach(([raza, r]) => {
          filas.push({ key: `r-${cat}-${raza}`, nivel: 1, grupo: raza, vendidos: r.vendidos, importe: r.importe });
        });
    });
  return { filas, totalVendidos, totalImporte };
};

const columnasResumen = [
  {
    title: 'Categoría / Raza', dataIndex: 'grupo', key: 'grupo',
    // La jerarquía se lee por la sangría y la negrita, como en el PDF.
    render: (v, r) => (
      <span style={{ paddingLeft: r.nivel ? 20 : 0, fontWeight: r.nivel ? 400 : 600 }}>{v}</span>
    ),
  },
  { title: 'Vendidos', dataIndex: 'vendidos', key: 'vendidos', align: 'right', width: 110 },
  { title: 'Total', dataIndex: 'importe', key: 'importe', align: 'right', width: 160,
    render: (v, r) => <span style={{ fontWeight: r.nivel ? 400 : 600 }}>{money(v)}</span> },
];

// A nivel de módulo, no adentro de `VentaAnimales`: definido en el render se
// remontaría la tabla entera en cada tecla del filtro de RP.
const ResumenVentas = ({ animales }) => {
  const { filas, totalVendidos, totalImporte } = resumirVentas(animales);
  if (filas.length === 0) return null;
  return (
    <Card
      size="small"
      title="Totales por categoría y raza"
      style={{ marginTop: 16, borderRadius: 8 }}
      extra={<Text type="secondary" style={{ fontSize: 12 }}>Sobre los animales que se ven en la tabla</Text>}
    >
      <Table
        size="small"
        columns={columnasResumen}
        dataSource={filas}
        pagination={false}
        rowKey="key"
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}><Text strong>Total de ventas</Text></Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right"><Text strong>{totalVendidos}</Text></Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right"><Text strong>{money(totalImporte)}</Text></Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </Card>
  );
};

const VentaAnimales = () => {
  // Solapa activa: 'todos' | 'evento' | 'transitoria' | 'match'
  const [tab, setTab] = useState('todos');
  const [eventos, setEventos] = useState([]);
  const [eventoId, setEventoId] = useState(null);
  const [animales, setAnimales] = useState([]);
  const [loadingEventos, setLoadingEventos] = useState(true);
  const [loadingAnimales, setLoadingAnimales] = useState(false);
  const [animalParaVenta, setAnimalParaVenta] = useState(null);
  const [ventaEnEdicion, setVentaEnEdicion] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [exportando, setExportando] = useState(false);

  const handleExportarExcel = async () => {
    setExportando(true);
    try {
      const res = await ventaService.exportarExcel(eventoId);
      descargarBlob(res, `ventas_${eventoId || 'todas'}.xlsx`);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al exportar a Excel');
    } finally {
      setExportando(false);
    }
  };

  // Filtros de la tabla (modo todos)
  const [filtroRp, setFiltroRp] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState(null);
  // El Estado era un filtro de columna de antd (`filters` + `onFilter`), que
  // filtra ADENTRO del Table: `animalesFiltrados` no lo veia, y el resumen de
  // abajo seguia sumando los que la tabla ya habia escondido. Los tres filtros
  // viven aca, en una sola fuente, y alimentan la tabla Y el resumen.
  const [filtroEstado, setFiltroEstado] = useState(null);

  useEffect(() => {
    const cargarEventos = async () => {
      setLoadingEventos(true);
      try {
        // lista-corta y NO getAll: getAll pagina de a 10 por defecto, así que
        // la cabaña con más de diez remates NO veía los viejos en el selector
        // —ni acá, ni en Venta transitoria, ni en Match, que reciben esta
        // misma lista por prop. Los tres pintan sólo id_evento y
        // nombre_evento, que lista-corta trae, sin paginar.
        const res = await eventoService.getListaCorta();
        setEventos(eventosDeListaCorta(res));
      } catch (error) {
        message.error(error.response?.data?.error || 'Error al cargar eventos');
      } finally {
        setLoadingEventos(false);
      }
    };
    cargarEventos();
  }, []);

  const cargarAnimales = useCallback(async () => {
    // La solapa transitoria maneja su propia carga (grilla).
    if (tab === 'transitoria' || tab === 'match') return;
    setLoadingAnimales(true);
    try {
      let res;
      if (tab === 'todos') {
        res = await animalService.getAllVenta({ per_page: 1000 });
      } else {
        if (!eventoId) { setAnimales([]); return; }
        res = await animalService.getByEventoVenta(eventoId);
      }
      setAnimales(res.data?.animales || res.data || []);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar animales');
    } finally {
      setLoadingAnimales(false);
    }
  }, [tab, eventoId]);

  useEffect(() => {
    cargarAnimales();
  }, [cargarAnimales]);

  // Resetear la navegación SOLO al cambiar de solapa/evento — NO después de una
  // venta. Así, tras guardar, la navegación por tarjeta se queda en el corral/
  // animal actual en vez de saltar al principio.
  useEffect(() => {
    setCurrentIndex(0);
  }, [tab, eventoId]);

  // Unidades de navegación (corrales agrupados + animales sueltos)
  const unidades = useMemo(() => construirUnidades(animales), [animales]);
  const unidadActual = unidades[currentIndex];

  // Opciones y filtrado de la tabla (modo todos)
  const categoriaOpciones = useMemo(() => {
    const set = new Set();
    animales.forEach((a) => a.categoria?.nom_categoria && set.add(a.categoria.nom_categoria));
    return [...set].sort();
  }, [animales]);

  const animalesFiltrados = useMemo(() => {
    const rp = filtroRp.trim().toLowerCase();
    return animales.filter((a) => {
      const okRp = !rp || (a.rp_animal || '').toLowerCase().includes(rp);
      const okCat = !filtroCategoria || a.categoria?.nom_categoria === filtroCategoria;
      const okEstado = !filtroEstado || estadoVenta(a) === filtroEstado;
      return okRp && okCat && okEstado;
    });
  }, [animales, filtroRp, filtroCategoria, filtroEstado]);

  const columnasTodos = [
    {
      title: 'Acción',
      key: 'accion',
      width: 130,
      render: (_, record) => {
        const vendido = (record.pct_vendido || 0) >= 100;
        return (
          <Button
            type={vendido ? 'default' : 'primary'}
            size="small"
            icon={vendido ? <EditOutlined /> : <ShoppingCartOutlined />}
            onClick={() => setAnimalParaVenta(record)}
          >
            {vendido ? 'Ver / editar' : 'Vender'}
          </Button>
        );
      },
    },
    { title: 'Corral', dataIndex: 'corral', key: 'corral', width: 80, render: (v) => v ?? '—' },
    { title: 'RP', dataIndex: 'rp_animal', key: 'rp_animal', width: 100 },
    { title: 'Nombre', dataIndex: 'nom_animal', key: 'nom_animal' },
    { title: 'Raza', dataIndex: 'nom_raza', key: 'nom_raza', render: (v) => v ? <Tag color="blue">{v}</Tag> : '—' },
    { title: 'Categoría', key: 'categoria', render: (_, r) => r.categoria?.nom_categoria ? <Tag color="geekblue">{r.categoria.nom_categoria}</Tag> : '—' },
    {
      title: 'Estado',
      key: 'estado',
      width: 120,
      render: (_, r) => {
        const estado = estadoVenta(r);
        if (estado === 'vendido') return <Tag color="red">Vendido</Tag>;
        if (estado === 'parcial') return <Tag color="orange">Parcial {r.pct_vendido}%</Tag>;
        return <Tag color="green">Disponible</Tag>;
      },
    },
    // Lo vendido de CADA animal, al lado de su estado: hasta hoy el importe
    // sólo se veía abriendo el animal. Con esto la tabla suma sola (ver el
    // resumen de abajo).
    {
      title: 'Vendido', key: 'vendido', align: 'right', width: 140,
      sorter: (a, b) => importeVendido(a) - importeVendido(b),
      render: (_, r) => { const v = importeVendido(r); return v ? money(v) : '—'; },
    },
  ];

  // ── Contenido de cada solapa ────────────────────────────────────────────────
  const tabTodos = (
    <>
      {loadingAnimales ? (
        <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>
      ) : (
        <>
          <Row gutter={12} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={8}>
              <Input
                allowClear
                placeholder="Filtrar por RP..."
                value={filtroRp}
                onChange={(e) => setFiltroRp(e.target.value)}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Select
                allowClear
                placeholder="Filtrar por categoría..."
                value={filtroCategoria}
                onChange={setFiltroCategoria}
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="children"
              >
                {categoriaOpciones.map((c) => (
                  <Option key={c} value={c}>{c}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Select
                allowClear
                placeholder="Filtrar por estado..."
                value={filtroEstado}
                onChange={setFiltroEstado}
                style={{ width: '100%' }}
                options={ESTADOS_VENTA}
              />
            </Col>
          </Row>
          <Table
            scroll={{ x: 'max-content' }}
            dataSource={animalesFiltrados}
            columns={columnasTodos}
            rowKey="id_animal"
            size="middle"
            pagination={{ pageSize: 50, showSizeChanger: true }}
            locale={{ emptyText: <Empty description="No hay animales" /> }}
          />
          <ResumenVentas animales={animalesFiltrados} />
        </>
      )}
    </>
  );

  const tabEvento = (
    <>
      <Card style={{ marginBottom: 16, borderRadius: 8 }}>
        <Row align="middle" gutter={12}>
          <Col flex="auto">
            <Select
              loading={loadingEventos}
              placeholder="Seleccioná un evento..."
              value={eventoId}
              onChange={setEventoId}
              style={{ minWidth: 300 }}
              allowClear
            >
              {eventos.map((e) => (
                <Option key={e.id_evento} value={e.id_evento}>{e.nombre_evento}</Option>
              ))}
            </Select>
          </Col>
          {eventoId && (
            <Col>
              <Button icon={<FileExcelOutlined />} loading={exportando} onClick={handleExportarExcel}>
                Exportar Excel
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {loadingAnimales ? (
        <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>
      ) : !eventoId ? (
        <Empty description="Seleccioná un evento para ver los animales" />
      ) : animales.length === 0 ? (
        <Empty description="No hay animales en este evento" />
      ) : unidadActual ? (
        unidadActual.tipo === 'corral' && unidadActual.animales.length > 1 ? (
          <CorralCard
            key={`corral-${unidadActual.corral}`}
            corral={unidadActual.corral}
            animales={unidadActual.animales}
            onVentaCreada={cargarAnimales}
            onEditarVenta={setVentaEnEdicion}
            currentIndex={currentIndex}
            total={unidades.length}
            onPrev={() => setCurrentIndex((i) => i - 1)}
            onNext={() => setCurrentIndex((i) => i + 1)}
          />
        ) : (
          <AnimalCard
            key={`animal-${(unidadActual.animal || unidadActual.animales[0]).id_animal}`}
            animal={unidadActual.animal || unidadActual.animales[0]}
            onVentaCreada={cargarAnimales}
            onEditarVenta={setVentaEnEdicion}
            currentIndex={currentIndex}
            total={unidades.length}
            onPrev={() => setCurrentIndex((i) => i - 1)}
            onNext={() => setCurrentIndex((i) => i + 1)}
          />
        )
      ) : null}
    </>
  );

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 16 }}>Venta de Animales</Title>

      <Tabs
        activeKey={tab}
        onChange={(k) => { setTab(k); if (k === 'todos') setEventoId(null); }}
        items={[
          { key: 'todos', label: 'Todos', children: tabTodos },
          { key: 'evento', label: 'Vista de Evento', children: tabEvento },
          {
            key: 'transitoria',
            label: 'Venta transitoria',
            children: <GrillaTransitoria eventos={eventos} loadingEventos={loadingEventos} />,
          },
          {
            key: 'match',
            label: 'Match de venta',
            children: <MatchVenta eventos={eventos} loadingEventos={loadingEventos} />,
          },
        ]}
      />

      {/* Modal de venta (solapa todos) */}
      <ModalVenta
        animal={animalParaVenta}
        onClose={() => setAnimalParaVenta(null)}
        onVentaCreada={cargarAnimales}
        onEditarVenta={setVentaEnEdicion}
      />

      {/* Modal de edición / eliminación de una venta */}
      <ModalEditarVenta
        venta={ventaEnEdicion}
        onClose={() => setVentaEnEdicion(null)}
        onGuardado={cargarAnimales}
      />
    </div>
  );
};

export default VentaAnimales;
